/**
 * صفحه‌ی عمومی «درگاه پرداخت رمزارز»
 * ===================================
 * برای فروشندگانی که هنوز کاربر ارزینو نیستند: چطور کار می‌کند، چه چیزی
 * لازم دارند، ماژول فروشگاهشان کجاست و چطور نصبش کنند.
 *
 * عمداً عمومی است (بدون نیاز به ورود): کسی که می‌خواهد تصمیم بگیرد، باید
 * بتواند اول بخواند. برای گوگل هم همین صفحه است که پیدا می‌شود.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Store, Download, ShieldCheck, Zap, Wallet, FileCode, ChevronDown,
  CheckCircle2, ArrowLeft,
} from 'lucide-react'
import { Shell } from '../components/Layout'
import { Card, SectionHead, Badge, Button } from '../components/UI'
import { get } from '../lib/api'
import { useSeo } from '../lib/seo'
import { clsx, num, asArray } from '../lib/format'

const SEO = {
  title: 'درگاه پرداخت رمزارز برای فروشگاه‌ها',
  description:
    'با درگاه پذیرندگی ارزینو، مشتریان فروشگاه شما فاکتورهای تومانی را با تتر، بیت‌کوین و اتریوم پرداخت می‌کنند. ماژول آماده ووکامرس، WHMCS، پرستاشاپ و اوپن‌کارت.',
  keywords: 'درگاه پرداخت رمزارز، درگاه کریپتو، پرداخت تتر، ماژول ووکامرس رمزارز، درگاه پرداخت ارز دیجیتال',
  canonical: '/merchants',
}

const STEPS: [string, string][] = [
  ['حساب ارزینو بسازید و احراز هویت کنید',
   'همان حسابی که پول به آن می‌نشیند. بدون احراز هویت، درگاه ساخته نمی‌شود.'],
  ['در پنل، بخش «درگاه پذیرندگی» یک درگاه بسازید',
   'یک API Key و یک Secret می‌گیرید. رمز فقط یک بار نشان داده می‌شود.'],
  ['ماژول فروشگاهتان را از همین صفحه دانلود و نصب کنید',
   'کلید و رمز را در تنظیمات ماژول بگذارید و فعالش کنید.'],
  ['تمام — مشتری با رمزارز پرداخت می‌کند',
   'مبلغ در لحظه قفل می‌شود، پول به موجودی شما می‌نشیند و سفارش خودکار پرداخت‌شده می‌شود.'],
]

const FAQ: [string, string][] = [
  ['مبلغ فاکتور تومانی است، مشتری چطور رمزارز می‌فرستد؟',
   'وقتی مشتری ارزش را انتخاب می‌کند، قیمت لحظه‌ای قفل می‌شود و مبلغ دقیق رمزارز به او نشان داده می‌شود. تا پایان مهلت، همان مبلغ معتبر است.'],
  ['اگر قیمت وسط پرداخت تغییر کند چه می‌شود؟',
   'ریسک نوسان با شماست نه با مشتری: مبلغ قفل‌شده تا انقضای فاکتور ثابت می‌ماند. اگر مهلت تمام شود، فاکتور منقضی و مبلغ تازه‌ای محاسبه می‌شود.'],
  ['پول کِی به دستم می‌رسد؟',
   'به‌محض تأیید تراکنش روی شبکه، به موجودی رمزارزی شما در ارزینو می‌نشیند. از همان‌جا می‌توانید به تومان تبدیل و برداشت کنید.'],
  ['اگر مشتری کمتر یا بیشتر بفرستد؟',
   'هر فاکتور یک مبلغ یکتا دارد و فقط پرداخت دقیق، خودکار تطبیق می‌خورد. پرداخت ناقص یا اضافه در پنل نشان داده می‌شود تا دستی رسیدگی کنید — پول در هیچ حالتی گم نمی‌شود.'],
  ['فروشگاه من روی ریال است، نه تومان.',
   'ماژول‌ها این را تشخیص می‌دهند و تبدیل می‌کنند. در تنظیمات هر ماژول گزینه‌ی «واحد پول فروشگاه» هست؛ اگر ارز سفارشی دارید، حتماً دستی مشخصش کنید.'],
  ['سیستم من هیچ‌کدام از این‌ها نیست.',
   'کتابخانه‌ی PHP و مستندات کامل REST را دانلود کنید. سه فراخوان بیشتر لازم نیست و با هر زبانی که HTTP بزند کار می‌کند.'],
]

function Faq({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-white/[.06] last:border-0">
      <button onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between gap-3 py-4 text-right">
        <span className="text-sm font-medium text-slate-200">{q}</span>
        <ChevronDown className={clsx('h-4 w-4 shrink-0 text-slate-500 transition', open && 'rotate-180')} />
      </button>
      {open && <p className="pb-4 text-xs leading-7 text-slate-400">{a}</p>}
    </div>
  )
}

export default function Merchants() {
  const [mods, setMods] = useState<any[] | null>(null)
  const [fee, setFee] = useState<number | null>(null)

  useEffect(() => {
    get('/merchant/modules')
      .then((r: any) => { setMods(asArray(r, 'modules')); setFee(typeof r?.feePct === 'number' ? r.feePct : null) })
      .catch(() => setMods([]))
  }, [])

  useSeo({
    ...SEO,
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQ.map(([q, a]) => ({
        '@type': 'Question', name: q,
        acceptedAnswer: { '@type': 'Answer', text: a },
      })),
    },
  }, [])

  return (
    <Shell>
      <div className="mx-auto max-w-5xl px-4 py-10">

        {/* ── سربرگ */}
        <div className="mb-10 text-center">
          <span className="mb-4 inline-grid h-14 w-14 place-items-center rounded-2xl border border-brand-500/25 bg-brand-500/10 text-brand-300">
            <Store className="h-6 w-6" />
          </span>
          <h1 className="text-2xl font-bold text-white lg:text-3xl">
            درگاه پرداخت رمزارز برای فروشگاه شما
          </h1>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-8 text-slate-400">
            مشتریان‌تان فاکتورهای تومانی را با تتر، بیت‌کوین یا اتریوم پرداخت می‌کنند.
            مبلغ در لحظه قفل می‌شود، پول به موجودی حساب ارزینوی شما می‌نشیند و سفارش
            خودکار «پرداخت‌شده» می‌شود.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to="/merchant"><Button>ساخت درگاه در پنل</Button></Link>
            <a href="#modules"><Button variant="ghost"><Download className="h-4 w-4" /> دانلود ماژول‌ها</Button></a>
          </div>
          {fee !== null && (
            <p className="mt-4 text-xs text-slate-500">
              کارمزد ارزینو از هر پرداخت: <b className="num text-slate-300">{num(fee, 2)}٪</b>
            </p>
          )}
        </div>

        {/* ── مزیت‌ها */}
        <div className="mb-12 grid gap-4 md:grid-cols-3">
          {[
            [Zap, 'تسویه‌ی آنی', 'به‌محض تأیید تراکنش روی شبکه، پول در موجودی شماست — نه چند روز بعد.'],
            [ShieldCheck, 'بدون برگشت پرداخت', 'تراکنش رمزارزی برگشت‌ناپذیر است؛ چارج‌بک و ابطال معنا ندارد.'],
            [Wallet, 'تبدیل به تومان هر وقت خواستید', 'همان‌جا در ارزینو به تومان تبدیل و به حساب بانکی‌تان برداشت کنید.'],
          ].map(([Icon, title, text]: any) => (
            <Card key={title}>
              <Icon className="mb-3 h-5 w-5 text-brand-300" />
              <h3 className="mb-1.5 font-bold text-white">{title}</h3>
              <p className="text-xs leading-7 text-slate-400">{text}</p>
            </Card>
          ))}
        </div>

        {/* ── مراحل */}
        <SectionHead title="چهار قدم تا اولین پرداخت" />
        <div className="mb-12 grid gap-3 md:grid-cols-2">
          {STEPS.map(([title, text], i) => (
            <Card key={title} className="flex gap-3">
              <span className="num grid h-8 w-8 shrink-0 place-items-center rounded-xl border border-brand-500/25 bg-brand-500/10 text-sm font-bold text-brand-300">
                {i + 1}
              </span>
              <div>
                <h3 className="mb-1 text-sm font-bold text-white">{title}</h3>
                <p className="text-xs leading-7 text-slate-400">{text}</p>
              </div>
            </Card>
          ))}
        </div>

        {/* ── ماژول‌ها */}
        <div id="modules" className="scroll-mt-24">
          <SectionHead title="ماژول فروشگاه‌ها"
            subtitle="دانلود مستقیم — هر بسته شامل راهنمای نصب فارسی است" />
        </div>
        <div className="mb-12 grid gap-3 md:grid-cols-2">
          {mods === null && <Card><p className="text-xs text-slate-500">در حال بارگذاری…</p></Card>}
          {(mods || []).map((m) => (
            <Card key={m.slug} className="flex flex-col">
              <div className="mb-2 flex items-center gap-2">
                <FileCode className="h-4 w-4 text-brand-300" />
                <h3 className="font-bold text-white">{m.name}</h3>
                {!m.available && <Badge kind="muted">به‌زودی</Badge>}
              </div>
              <p className="mb-3 text-xs leading-7 text-slate-400">{m.summary}</p>
              <dl className="mb-4 space-y-1 text-[11px] text-slate-500">
                <div className="flex gap-2"><dt className="w-20 shrink-0">سازگار با</dt><dd>{m.platform}</dd></div>
                <div className="flex gap-2"><dt className="w-20 shrink-0">پیش‌نیاز</dt><dd>{m.requires}</dd></div>
                <div className="flex gap-2"><dt className="w-20 shrink-0">محل نصب</dt>
                  <dd className="num" dir="ltr">{m.install}</dd></div>
              </dl>
              {m.available ? (
                <a href={m.download} download
                  className="mt-auto inline-flex items-center justify-center gap-2 rounded-xl border border-brand-500/30 bg-brand-500/10 px-4 py-2.5 text-sm font-medium text-brand-200 transition hover:bg-brand-500/20">
                  <Download className="h-4 w-4" /> دانلود بسته
                </a>
              ) : (
                <span className="mt-auto rounded-xl border border-white/[.06] px-4 py-2.5 text-center text-sm text-slate-600">
                  هنوز آماده نیست
                </span>
              )}
            </Card>
          ))}
        </div>

        {/* ── نصب کوتاه */}
        <SectionHead title="نصب، خلاصه" subtitle="راهنمای کامل داخل هر بسته است" />
        <Card className="mb-12">
          <ol className="space-y-4">
            {[
              ['ووکامرس', 'پوشه را در wp-content/plugins/ بگذارید ← افزونه را فعال کنید ← ووکامرس ← تنظیمات ← پرداخت‌ها ← ارزینو ← کلید و رمز را وارد کنید.'],
              ['WHMCS', 'دو فایل را در modules/gateways/ و modules/gateways/callback/ بگذارید ← Setup ← Payments ← Payment Gateways ← ارزینو را فعال کنید.'],
              ['پرستاشاپ', 'پوشه‌ی arzino را در modules/ بگذارید ← از Module Manager نصب کنید ← پیکربندی ← کلید و رمز.'],
              ['اوپن‌کارت', 'محتویات upload را روی ریشه کپی کنید ← Extensions ← Payments ← ارزینو ← نصب و ویرایش.'],
              ['سایر سیستم‌ها', 'ArzinoClient.php را در پروژه بگذارید و مستندات REST را بخوانید — سه فراخوان بیشتر نیست.'],
            ].map(([name, text]) => (
              <li key={name} className="flex gap-3">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-up" />
                <div>
                  <b className="text-sm text-white">{name}</b>
                  <p className="mt-0.5 text-xs leading-7 text-slate-400">{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </Card>

        {/* ── هشدار واحد پول */}
        <Card className="mb-12 border-gold/25 bg-gold/[.05]">
          <h3 className="mb-2 font-bold text-white">یک نکته که حتماً بخوانید: ریال یا تومان</h3>
          <p className="text-xs leading-7 text-slate-300">
            ارزینو مبلغ را همیشه به <b>تومان</b> می‌گیرد. اگر فروشگاه شما روی ریال تنظیم
            است و این تبدیل انجام نشود، مشتری یک‌دهمِ مبلغ را می‌پردازد. ماژول‌های ما
            واحد را از خود فروشگاه تشخیص می‌دهند؛ ولی اگر با افزونه‌ای ارز سفارشی
            ساخته‌اید، در تنظیمات ماژول گزینه‌ی «واحد پول فروشگاه» را دستی مشخص کنید.
          </p>
        </Card>

        {/* ── پرسش‌ها */}
        <SectionHead title="پرسش‌های پرتکرار" />
        <Card className="!p-0">
          <div className="px-5">
            {FAQ.map(([q, a]) => <Faq key={q} q={q} a={a} />)}
          </div>
        </Card>

        <div className="mt-10 text-center">
          <Link to="/merchant"
            className="inline-flex items-center gap-2 text-sm text-brand-300 hover:underline">
            ساخت درگاه در پنل کاربری <ArrowLeft className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </Shell>
  )
}
